const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000";

export interface ApiError {
  message: string;
  statusCode: number;
  error?: string;
}

class ApiService {
  /**
   * Core fetch method with authentication via HTTP-only cookies
   */
  async fetch<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    let response: Response;

    try {
      response = await fetch(`${API_URL}${endpoint}`, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          ...options.headers,
        },
        credentials: "include", // Important: This sends cookies with requests
      });
    } catch (error) {
      // Network error (connection refused, timeout, etc.)
      const apiError: ApiError = {
        message:
          "Sunucuya bağlanılamadı. Lütfen backend sunucusunun çalıştığından emin olun.",
        statusCode: 0,
        error: error instanceof Error ? error.message : "Network Error",
      };
      throw apiError;
    }

    if (!response.ok) {
      const error: ApiError = await response.json().catch(() => ({
        message: "Bir hata oluştu",
        statusCode: response.status,
      }));

      // Handle 401 - Unauthorized
      if (response.status === 401) {
        // Auth uçlarında 401 "oturum düştü" demek değildir: yanlış şifre de
        // 401 döner ve burada yönlendirilirse sayfa yeniden yüklenip formdaki
        // hata mesajı kaybolur. Bu uçların 401'i çağırana aittir.
        const isAuthEndpoint = endpoint.startsWith("/auth/");

        if (!isAuthEndpoint) {
          // Try to refresh token
          const refreshed = await this.tryRefreshToken();
          if (refreshed) {
            // Retry the original request
            return this.fetch<T>(endpoint, options);
          }

          // Redirect to login if refresh failed
          if (typeof window !== "undefined") {
            const currentPath = window.location.pathname;
            // Extract locale from current path (e.g., /tr/dashboard -> tr)
            const pathParts = currentPath.split("/").filter(Boolean);
            const locale = pathParts[0] || "tr";
            window.location.href = `/${locale}/login?redirect=${encodeURIComponent(currentPath)}`;
          }
        }
      }

      throw error;
    }

    // Handle empty responses (204 No Content)
    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }

  /**
   * Try to refresh the access token using refresh token cookie
   */
  private async tryRefreshToken(): Promise<boolean> {
    try {
      const response = await fetch(`${API_URL}/auth/refresh`, {
        method: "POST",
        credentials: "include",
      });
      return response.ok;
    } catch {
      return false;
    }
  }

  get<T>(endpoint: string): Promise<T> {
    return this.fetch<T>(endpoint);
  }

  post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.fetch<T>(endpoint, {
      method: "POST",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.fetch<T>(endpoint, {
      method: "PUT",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  patch<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.fetch<T>(endpoint, {
      method: "PATCH",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  delete<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.fetch<T>(endpoint, {
      method: "DELETE",
      body: data ? JSON.stringify(data) : undefined,
    });
  }
}

export const api = new ApiService();

/**
 * Transform backend response format (meta) to frontend format (pagination).
 * Handles both: raw backend { data, meta } and ResponseInterceptor-wrapped { data: { data, meta } }.
 */
export function transformPaginatedResponse<T>(
  response: {
    data?: T[] | { data?: T[]; meta?: unknown };
    meta?: unknown;
    pagination?: unknown;
  },
  options?: { page?: number; limit?: number },
): {
  success: boolean;
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
} {
  const inner = response.data;
  const list: T[] = Array.isArray(inner)
    ? inner
    : ((inner as { data?: T[] })?.data ?? []);
  const meta =
    (inner as { meta?: Record<string, number> })?.meta ??
    (response.meta as Record<string, number>) ??
    response.pagination;
  const defaultPagination = {
    page: options?.page || 1,
    limit: options?.limit || 10,
    total: 0,
    totalPages: 0,
  };
  const pagination =
    meta &&
    typeof meta === "object" &&
    typeof (meta as Record<string, number>).total === "number"
      ? { ...defaultPagination, ...(meta as Record<string, number>) }
      : defaultPagination;
  return {
    success: true,
    data: list,
    pagination,
  };
}
